/**
 * 50 cas pratiques du livre "L'IA au service des enseignants"
 * Répartition : 5 chapitres × 10 cas = 50
 *  - 8 cas testables en 1 clic dans l'app (type: "testable")
 *      → pré-remplissent le Dashboard avec un texte source + format + niveau
 *  - 42 cas avec prompts copiables (type: "prompt")
 *      → le prompt se copie dans le presse-papier
 *
 * Chaque cas correspond à un cas réel du livre, numéroté 1-50.
 */

export const CHAPTERS = [
  { id: "preparer", label: "Préparer un cours", short: "Préparer", color: "from-blue-500 to-blue-700" },
  { id: "corriger", label: "Corriger les copies", short: "Corriger", color: "from-red-500 to-red-700" },
  { id: "differencier", label: "Différencier (DYS · HPI · Allophones)", short: "Différencier", color: "from-purple-500 to-purple-700" },
  { id: "bulletins", label: "Bulletins et communication parents", short: "Bulletins & parents", color: "from-green-600 to-green-800" },
  { id: "carriere", label: "Carrière (PPCR · Mobilité · Formation)", short: "Carrière", color: "from-amber-500 to-amber-700" },
];

// Example source text used by testable cases. ~500-800 chars so demo stays fast.
const SAMPLE_TEXTS = {
  photosynthese: `La photosynthèse est le processus biologique par lequel les plantes vertes transforment la lumière solaire, le dioxyde de carbone et l'eau en glucose et en oxygène grâce à la chlorophylle contenue dans les chloroplastes. Elle se déroule en deux phases : la phase claire (dépendante de la lumière, dans les thylakoïdes) qui produit l'ATP et le NADPH ; et la phase sombre (cycle de Calvin, dans le stroma) qui fixe le CO2 pour produire du glucose. Ce mécanisme est à la base de la chaîne alimentaire terrestre et libère l'oxygène qui compose 21% de notre atmosphère. Sans photosynthèse, la vie telle que nous la connaissons n'existerait pas sur Terre.`,
  revolution: `La Révolution française de 1789 marque la fin de l'Ancien Régime en France. Déclenchée par une crise financière et sociale aggravée par la convocation des États généraux, elle aboutit à la prise de la Bastille le 14 juillet 1789. La Déclaration des droits de l'homme et du citoyen, adoptée le 26 août 1789, proclame l'égalité, la liberté et la souveraineté du peuple. La monarchie absolue est abolie en 1792 avec la proclamation de la Première République. L'exécution de Louis XVI en janvier 1793 et la Terreur (1793-1794) sous Robespierre marquent les phases radicales. La Révolution se solde par l'avènement de Napoléon Bonaparte en 1799 et transforme durablement la société française et européenne.`,
  dissertation: `Sujet : « Le travail nous rend-il libre ? »
Proposition d'élève : Le travail est à la fois une contrainte qui nous aliène (Marx) et un moyen d'émancipation (Hegel). Il nous rend libre quand il nous permet d'exprimer notre humanité et de transformer le monde, mais il nous asservit quand il devient simple moyen de subsistance ou outil d'exploitation. La liberté par le travail dépend donc moins du travail lui-même que des conditions dans lesquelles il s'exerce.`,
  pythagore: `Le théorème de Pythagore établit que dans un triangle rectangle, le carré de l'hypoténuse est égal à la somme des carrés des deux autres côtés : a² + b² = c². Exemple : si a = 3 et b = 4, alors c² = 9 + 16 = 25, donc c = 5. Ce théorème, démontré par Pythagore au VIe siècle avant J.-C., est à la base de la trigonométrie et de nombreuses applications pratiques (architecture, navigation, informatique).`,
  copie_eleve: `Copie de Léa, 4e, Histoire-Géographie — Sujet : Décrivez les causes de la Première Guerre mondiale.
La Première Guerre mondiale a commencé en 1914 à cause de plusieurs raisons. D'abord, les alliances entre les pays : la Triple Alliance avec l'Allemagne, l'Autriche-Hongrie et l'Italie contre la Triple Entente avec la France, le Royaume-Uni et la Russie. Ensuite, les tensions coloniales en Afrique et en Asie. Mais l'évènement déclencheur c'est l'assassinat de l'archiduc François-Ferdinand à Sarajevo le 28 juin 1914 par un nationaliste serbe. Après ça, les pays se sont tous déclaré la guerre à cause des alliances. La guerre a duré 4 ans et a fait des millions de morts.`,
};

export const CAS = [
  // ============== CHAPITRE 1 — PRÉPARER UN COURS (10 cas) ==============
  {
    id: 1,
    chapter: "preparer",
    title: "Résumer un chapitre de manuel en 2 min",
    teaser: "Transformez un chapitre de 20 pages en résumé-fiche prêt à relire avant le cours.",
    type: "testable",
    sampleText: SAMPLE_TEXTS.photosynthese,
    format: "enseignant",
    audienceLevel: "lycee",
    model: "claude-sonnet-4-5",
    cta: "Tester avec un extrait de SVT",
  },
  {
    id: 2,
    chapter: "preparer",
    title: "Transformer un cours en plan de séance 55 min",
    teaser: "Objectifs, matériel, déroulé minuté, activités, évaluation, différenciation.",
    type: "testable",
    sampleText: SAMPLE_TEXTS.revolution,
    format: "enseignant",
    audienceLevel: "college",
    model: "claude-sonnet-4-5",
    cta: "Tester avec la Révolution française",
  },
  {
    id: 3,
    chapter: "preparer",
    title: "Générer 10 questions ouvertes pour lancer un débat",
    teaser: "À partir d'un texte ou d'un chapitre, obtenir 10 questions ouvertes graduées.",
    type: "prompt",
    prompt: "Génère 10 questions ouvertes à partir de ce texte, graduées de la plus simple (compréhension) à la plus complexe (analyse critique). Pour chaque question : (1) l'objectif pédagogique (restituer / analyser / évaluer / créer), (2) les 2-3 pistes de réponses attendues en 1-2 lignes chacune pour animer le débat, (3) une relance possible si les élèves bloquent.",
  },
  {
    id: 4,
    chapter: "preparer",
    title: "Créer une trace écrite synthétique",
    teaser: "Produire une trace écrite claire avec titres, mots-clés en gras et schéma mental.",
    type: "testable",
    sampleText: SAMPLE_TEXTS.revolution,
    format: "enseignant",
    audienceLevel: "college",
    model: "gpt-5.2",
    cta: "Tester avec la Révolution française",
  },
  {
    id: 5,
    chapter: "preparer",
    title: "Préparer un cours à partir d'une photo de manuel",
    teaser: "Photographiez une page de manuel, laissez l'IA en extraire le cours structuré.",
    type: "prompt",
    prompt: "À partir de la photo ou du PDF de cette page de manuel, extrais : (1) la notion clé en une phrase, (2) 5 définitions essentielles, (3) 3 exemples pédagogiques pour les élèves, (4) 2 pièges fréquents à éviter, (5) une question de synthèse pour la fin du cours AVEC la réponse attendue en 3-4 lignes à destination du professeur.",
  },
  {
    id: 6,
    self_contained: true,
    chapter: "preparer",
    title: "Trouver 3 analogies pour expliquer une notion abstraite",
    teaser: "Format Métaphores : chaque idée clé expliquée par une image du quotidien (« C'est comme… ») et sa limite.",
    type: "testable",
    sampleText: "les fractions",
    format: "metaphore",
    audienceLevel: "college",
    model: "gemini-3-flash",
    cta: "Tester avec « les fractions »",
    prompt: "Pour expliquer la notion [NOTION] à des élèves de [NIVEAU], propose 3 analogies issues de leur quotidien (sport, jeux vidéo, réseaux sociaux, vie de famille…). Pour chaque analogie : explique le parallèle, les limites, et une phrase à dire en classe pour l'introduire.",
  },
  {
    id: 7,
    self_contained: true,
    chapter: "preparer",
    title: "Créer un escape game pédagogique",
    teaser: "Scénarisez un escape game de 45 min autour d'une notion de cours.",
    type: "prompt",
    prompt: "Scénarise un escape game pédagogique de 45 minutes sur le thème [THEME] pour une classe de [NIVEAU]. Fournis : scénario de départ, 5 énigmes graduées (chaque énigme révèle un indice pour la suivante), matériel nécessaire, correction, variantes de difficulté. L'énigme finale doit permettre de récapituler la leçon.",
  },
  {
    id: 8,
    chapter: "preparer",
    title: "Rédiger une consigne claire en 3 étapes",
    teaser: "Transformer une consigne confuse en 3 étapes limpides pour tous les élèves.",
    type: "prompt",
    prompt: "Réécris cette consigne en 3 étapes claires, numérotées, avec des verbes d'action à l'infinitif. Vérifie qu'un élève DYS puisse la comprendre sans aide. Précise à la fin : le temps estimé, les critères de réussite (3 maxi), et le livrable attendu.",
  },
  {
    id: 9,
    chapter: "preparer",
    title: "Préparer un cours flipped (classe inversée)",
    teaser: "Diviser un cours en vidéo-maison / activité-classe / synthèse-collective.",
    type: "prompt",
    prompt: "Transforme ce chapitre en séance de classe inversée pour [NIVEAU]. Fournis : (1) scénario d'une vidéo de 7 minutes à regarder à la maison avec script, (2) activité collaborative de 30 minutes en classe (consigne, matériel, rôles), (3) synthèse collective de 15 minutes avec questions-réponses, (4) évaluation formative express de 10 minutes.",
  },
  {
    id: 10,
    chapter: "preparer",
    title: "Transformer un article de presse en support de cours",
    teaser: "L'IA extrait d'un article de presse ce qui est utilisable en EMC ou en Français.",
    type: "prompt",
    prompt: "À partir de cet article de presse, prépare un support de cours : (1) résumé en 5 lignes pour les élèves, (2) 3 mots de vocabulaire à expliquer avec leur définition, (3) 5 questions de compréhension graduées AVEC leur corrigé en 1-2 lignes chacun, (4) 2 questions de débat EMC AVEC les 2-3 pistes de réponses attendues et une relance possible, (5) une activité d'écriture (compte rendu, lettre, discours) adaptée au niveau [NIVEAU] avec critères d'évaluation.",
  },

  // ============== CHAPITRE 2 — CORRIGER LES COPIES (10 cas) ==============
  {
    id: 11,
    chapter: "corriger",
    title: "Corriger une copie en 2 min avec annotations",
    teaser: "L'IA annote la copie point par point : syntaxe, argumentation, pistes d'amélioration.",
    type: "testable",
    sampleText: SAMPLE_TEXTS.copie_eleve,
    format: "bullets",
    audienceLevel: "college",
    model: "claude-sonnet-4-5",
    cta: "Tester avec une copie type",
  },
  {
    id: 12,
    chapter: "corriger",
    title: "Créer une correction-type sur 20 avec barème",
    teaser: "Barème détaillé, critères explicites, exemples de réponses à chaque niveau.",
    type: "testable",
    sampleText: SAMPLE_TEXTS.dissertation,
    format: "philo",
    audienceLevel: "lycee",
    model: "gpt-5.2",
    cta: "Tester avec une dissertation",
  },
  {
    id: 13,
    chapter: "corriger",
    title: "Rédiger une appréciation personnalisée en 3 lignes",
    teaser: "À partir d'un résumé de la copie, obtenir une appréciation diplomate et personnalisée.",
    type: "prompt",
    prompt: "Rédige une appréciation personnalisée en 3 lignes pour cette copie, en respectant 4 principes : (1) commencer par un point positif concret, (2) nommer 1 point à travailler précisément, (3) proposer 1 piste d'action, (4) terminer par un encouragement sincère (pas de langue de bois). Ton bienveillant mais exigeant.",
  },
  {
    id: 14,
    chapter: "corriger",
    title: "Détecter l'usage de ChatGPT dans une copie",
    teaser: "L'IA identifie les indices qu'une copie a été générée par IA (tournures, patterns…).",
    type: "prompt",
    prompt: "Analyse cette copie et identifie si elle a probablement été générée par IA. Liste : (1) les tournures suspectes (listes trop parfaites, transitions trop fluides, vocabulaire trop large pour le niveau), (2) les éléments qui penchent vers une rédaction humaine (fautes, raisonnements maladroits, exemples personnels), (3) ton verdict probabiliste (0-100%) et (4) ta recommandation pédagogique.",
  },
  {
    id: 15,
    self_contained: true,
    chapter: "corriger",
    title: "Générer 30 appréciations variées pour un lot",
    teaser: "Évitez le « peut mieux faire » x30 : l'IA varie les formulations.",
    type: "prompt",
    prompt: "Je dois rédiger 30 appréciations pour une classe de [NIVEAU] en [MATIERE] après ce contrôle. Génère 30 variantes d'appréciations courtes (2-3 lignes chacune) couvrant : (1) 5 élèves excellents, (2) 10 élèves moyens-haut, (3) 10 élèves moyens-bas, (4) 5 élèves en difficulté. Chaque appréciation doit être unique (vocabulaire différent) et constructive.",
  },
  {
    id: 16,
    chapter: "corriger",
    title: "Corriger une dissertation philosophique",
    teaser: "Analyse de la problématisation, du plan, des références, et conseils d'amélioration.",
    type: "testable",
    sampleText: SAMPLE_TEXTS.dissertation,
    format: "philo",
    audienceLevel: "lycee",
    model: "claude-sonnet-4-5",
    cta: "Tester avec une dissertation",
  },
  {
    id: 17,
    chapter: "corriger",
    title: "Analyser les erreurs récurrentes d'une classe",
    teaser: "L'IA agrège les erreurs d'un lot de copies et propose un plan de remédiation.",
    type: "prompt",
    prompt: "Voici les erreurs relevées dans un lot de 30 copies de [NIVEAU] en [MATIERE]. Analyse ces erreurs et produis : (1) un classement des 5 erreurs les plus fréquentes, (2) l'origine probable de chaque erreur (compréhension, méthode, inattention), (3) une séance de remédiation de 55 minutes ciblant les 2 erreurs prioritaires avec objectifs, activités, trace écrite.",
  },
  {
    id: 18,
    chapter: "corriger",
    title: "Rédiger un corrigé pour un parent qui demande",
    teaser: "Version pédagogique du corrigé pour un parent qui veut aider à la maison.",
    type: "prompt",
    prompt: "Transforme ce corrigé-type (destiné à l'enseignant) en version pour un parent qui veut aider son enfant à la maison. Simplifie le vocabulaire, explique les attendus en langage courant, ajoute 2 conseils de méthode pour le prochain devoir. Ne donne pas la correction brute : donne les clés pour que le parent accompagne le raisonnement.",
  },
  {
    id: 19,
    chapter: "corriger",
    title: "Résoudre un exercice de maths avec explication",
    teaser: "L'IA résout l'exercice en explicitant chaque étape pour pouvoir le corriger ou l'enseigner.",
    type: "testable",
    sampleText: `Exercice : Un triangle ABC est rectangle en A. On a AB = 6 cm et AC = 8 cm. Calculer la longueur BC et vérifier que ce triangle est bien rectangle en A à l'aide du théorème de Pythagore. Donner la réponse au centième près si nécessaire.`,
    format: "solve",
    audienceLevel: "college",
    model: "gpt-5.2",
    cta: "Tester avec un exercice de maths",
  },
  {
    id: 20,
    chapter: "corriger",
    title: "Transformer une copie en fiche-révision",
    teaser: "L'IA utilise la copie corrigée pour générer une fiche personnalisée pour l'élève.",
    type: "prompt",
    prompt: "À partir de cette copie corrigée et de mes annotations, génère une fiche de révision personnalisée pour cet élève : (1) 3 points qu'il maîtrise (à consolider), (2) 2 points qu'il doit retravailler avec une méthode précise, (3) 1 exercice d'entraînement adapté à son niveau, (4) 1 encouragement spécifique. Format : 1 page A4, polices lisibles, mise en forme claire.",
  },

  // ============== CHAPITRE 3 — DIFFÉRENCIER (10 cas) ==============
  {
    id: 21,
    chapter: "differencier",
    title: "Adapter un texte pour un élève DYS",
    teaser: "Phrases courtes, vocabulaire simple, une idée par ligne, mots-clés en gras.",
    type: "testable",
    sampleText: SAMPLE_TEXTS.photosynthese,
    format: "bullets",
    audienceLevel: "college",
    model: "claude-sonnet-4-5",
    cta: "Tester avec un texte de SVT",
  },
  {
    id: 22,
    chapter: "differencier",
    title: "Produire 3 versions d'un contrôle (facile / moyen / difficile)",
    teaser: "Même notion, 3 niveaux de difficulté pour accueillir toute la classe.",
    type: "prompt",
    prompt: "À partir de ce contrôle, produis 3 versions graduées : (1) version Allégée pour élèves en difficulté (consignes simplifiées, calculs assistés, un exemple de résolution), (2) version Standard pour la majorité, (3) version Enrichie pour les élèves rapides (bonus de recherche, question ouverte, piste d'approfondissement). Les 3 versions évaluent le même objectif mais à des niveaux différents. Fournis EN FIN de document un corrigé commun qui couvre les 3 versions (avec, pour la question ouverte de la version Enrichie, 2-3 pistes de réponses attendues).",
  },
  {
    id: 23,
    chapter: "differencier",
    title: "Reformuler une consigne pour un élève allophone",
    teaser: "Vocabulaire A2-B1, phrases simples, pictogrammes suggérés.",
    type: "prompt",
    prompt: "Reformule cette consigne pour un élève allophone (niveau A2-B1 en français). Règles : phrases de 10 mots maximum, un verbe par phrase, remplace les mots abstraits par des synonymes concrets, propose un pictogramme (émoji) pour chaque étape. Ajoute à la fin un lexique de 5 mots-clés avec définition courte.",
  },
  {
    id: 24,
    chapter: "differencier",
    title: "Enrichir un exercice pour un élève HPI",
    teaser: "Ajouter une question ouverte, une question méta, et un défi bonus.",
    type: "prompt",
    prompt: "Enrichis cet exercice pour un élève HPI (Haut Potentiel Intellectuel) sans allonger la version standard : (1) une question méta en bas (« Pourquoi cette méthode fonctionne-t-elle ? ») avec la réponse attendue en 2-3 lignes à destination du professeur, (2) un défi bonus (« Trouve une autre méthode de résolution ») avec 1 ou 2 méthodes alternatives possibles détaillées pour le prof, (3) une ouverture culturelle (« Cette notion a été utilisée par qui, dans quel contexte historique ? ») avec 2-3 éléments de réponse factuels pour le prof. Garde l'exercice visible par l'élève sur une seule page ; les corrigés sont dans un bloc séparé « Pour le professeur ».",
  },
  {
    id: 25,
    self_contained: true,
    chapter: "differencier",
    title: "Préparer un PAP (Plan d'Accompagnement Personnalisé)",
    teaser: "Trame complète d'un PAP pour un élève DYS, prête à présenter en équipe.",
    type: "prompt",
    prompt: "Rédige un PAP (Plan d'Accompagnement Personnalisé) pour un élève de [NIVEAU] avec [TROUBLE]. Inclure : (1) adaptations en classe (temps majoré, place dans la classe, format des consignes), (2) adaptations pour les devoirs, (3) adaptations pour les évaluations, (4) outils de compensation (ordinateur, logiciels, dictée vocale), (5) partenariat avec les parents, (6) critères de suivi trimestriel.",
  },
  {
    id: 26,
    chapter: "differencier",
    title: "Créer un support audio pour un élève dyslexique",
    teaser: "Format Poème et récitation : la leçon en vers rimés, lue à voix haute strophe par strophe (gratuit).",
    type: "testable",
    sampleText: "le cycle de l'eau",
    format: "poeme",
    audienceLevel: "primaire",
    model: "gemini-3-flash",
    cta: "Tester avec « le cycle de l'eau »",
    prompt: "Transforme ce chapitre en script audio de 7 minutes maximum, pour un élève dyslexique qui préfère écouter que lire. Règles : phrases courtes, intonations marquées par la ponctuation (points, virgules, deux-points), répétitions des idées clés, pauses explicites toutes les 2-3 phrases. À la fin, ajoute un mini-quiz oral de 3 questions-réponses pour vérifier la compréhension.",
  },
  {
    id: 27,
    chapter: "differencier",
    title: "Rédiger une consigne multimodale",
    teaser: "Une consigne en texte + schéma + étapes numérotées, accessible à tous.",
    type: "prompt",
    prompt: "Rédige cette consigne en 3 modalités complémentaires : (1) version texte structurée en 3 étapes, (2) description d'un schéma simple à dessiner (flèches, cases, pictogrammes), (3) version orale à dicter en classe (phrases courtes, répétitions, questions de vérification AVEC la réponse attendue entre crochets pour que le prof puisse valider à la volée). L'élève doit pouvoir comprendre la consigne même en n'utilisant qu'une des 3 modalités.",
  },
  {
    id: 28,
    chapter: "differencier",
    title: "Proposer un parcours autonome pour un élève absent",
    teaser: "Fiche de rattrapage autonome sur 1 semaine à partir du cours manqué.",
    type: "prompt",
    prompt: "Un élève a manqué cette séance de cours. Crée un parcours de rattrapage autonome sur une semaine : (1) jour 1 : lecture du cours synthétique (fournir le texte en 400 mots max), (2) jour 2-3 : 3 exercices gradués avec autocorrection, (3) jour 4 : vérification de compréhension via 5 QCM, (4) jour 5 : exercice d'application à rendre au professeur. Inclure les critères pour que l'élève s'auto-évalue.",
  },
  {
    id: 29,
    chapter: "differencier",
    title: "Adapter un évaluation pour un élève à tiers-temps",
    teaser: "Réécrire l'épreuve en version équivalente mais compatible avec un tiers-temps.",
    type: "prompt",
    prompt: "Cette évaluation dure 55 minutes. Réécris-la pour un élève bénéficiant d'un tiers-temps (donc +18 minutes = 73 min, mais même nombre de points). Règles : simplifier les consignes sans baisser le niveau, réduire les tâches secondaires (recopier, mise en forme), concentrer sur les objectifs essentiels. Note : ne pas allonger artificiellement, mais rendre les 73 minutes utiles.",
  },
  {
    id: 30,
    chapter: "differencier",
    title: "Créer un lexique illustré pour un élève allophone",
    teaser: "20 mots-clés du chapitre avec traduction, phonétique, exemple, émoji.",
    type: "prompt",
    prompt: "À partir de ce chapitre, extrais 20 mots-clés indispensables à comprendre pour un élève allophone (niveau A2-B1). Pour chaque mot : (1) orthographe, (2) prononciation phonétique simple, (3) définition en 10 mots max, (4) exemple en phrase courte issue du cours, (5) un emoji ou pictogramme. Format tableau pour impression A4.",
  },

  // ============== CHAPITRE 4 — BULLETINS ET PARENTS (10 cas) ==============
  {
    id: 31,
    self_contained: true,
    chapter: "bulletins",
    title: "Rédiger un mail aux parents après un conseil de classe",
    teaser: "Ton diplomate, synthèse des échanges, pistes de progression, invitation au dialogue.",
    type: "prompt",
    prompt: "Rédige un mail diplomate aux parents après le conseil de classe du 1er trimestre. L'élève [PRENOM] est en difficulté en [MATIERE] mais fait des efforts. Structure : (1) salutation personnalisée, (2) 2-3 points positifs concrets, (3) 1-2 points de vigilance factuels, (4) 3 pistes d'action concrètes, (5) proposition de rendez-vous, (6) formule de politesse chaleureuse. Ton : respectueux, factuel, orienté solutions. 200 mots max.",
  },
  {
    id: 32,
    self_contained: true,
    chapter: "bulletins",
    title: "Préparer un entretien difficile avec un parent",
    teaser: "Script d'entretien, formules de désescalade, pistes de dialogue constructif.",
    type: "prompt",
    prompt: "Prépare-moi un script d'entretien avec un parent mécontent du traitement de son enfant [SITUATION]. Fournis : (1) 5 phrases d'accueil pour désamorcer la tension, (2) les 3 arguments factuels principaux à présenter (sans jugement), (3) 3 formules d'écoute active à utiliser, (4) les 2 compromis acceptables, (5) la ligne rouge à ne pas franchir, (6) une formulation de conclusion qui laisse la porte ouverte. Ton : professionnel, calme, empathique.",
  },
  {
    id: 33,
    self_contained: true,
    chapter: "bulletins",
    title: "Écrire une appréciation trimestrielle structurée",
    teaser: "Format pédagogique : constat + progrès + conseil + encouragement, en 2 lignes.",
    type: "prompt",
    prompt: "Rédige une appréciation trimestrielle pour l'élève [PRENOM] en [MATIERE] (moyenne : [NOTE]/20). Format imposé : (1) constat chiffré en 1 ligne, (2) progrès observé ou en attente, (3) conseil méthodologique précis, (4) encouragement sincère. Longueur : 2 lignes maximum. Ton : exigeant mais bienveillant. Interdit : « peut mieux faire », « doit progresser », « travail insuffisant » (langue de bois).",
  },
  {
    id: 34,
    self_contained: true,
    chapter: "bulletins",
    title: "Rédiger un compte rendu de réunion parents-profs",
    teaser: "Compte rendu synthétique prêt à envoyer aux parents absents.",
    type: "prompt",
    prompt: "Rédige un compte rendu de la réunion parents-professeurs du [DATE]. Il doit couvrir : (1) les sujets abordés (en 3-4 bullet points), (2) les décisions prises, (3) les actions à mener d'ici le prochain conseil, (4) les contacts utiles. Format : 1 page A4 maximum, mise en forme claire, signature de l'équipe pédagogique. Ton : factuel, professionnel, accessible aux parents non francophones.",
  },
  {
    id: 35,
    self_contained: true,
    chapter: "bulletins",
    title: "Expliquer un bulletin à un parent qui le comprend mal",
    teaser: "Décrypter les codes du bulletin (NA/EC/A/D, barème…) en langage simple.",
    type: "prompt",
    prompt: "Un parent me demande d'expliquer le bulletin de son enfant. Rédige un message qui décrypte : (1) ce que signifient les sigles NA/EC/A/D (ou A/B/C/D selon établissement), (2) comment lire une moyenne pondérée, (3) la différence entre un contrôle commun et un contrôle de classe, (4) comment interpréter les appréciations trimestrielles, (5) 3 questions pertinentes à poser aux professeurs lors d'un rendez-vous AVEC pour chacune l'intention (ce que le parent cherche à comprendre) et un exemple de réponse-type que le prof pourrait donner, pour que le parent se projette dans l'échange. Niveau de langue : simple, pédagogique, respectueux.",
  },
  {
    id: 36,
    self_contained: true,
    chapter: "bulletins",
    title: "Rédiger un signalement bienveillant",
    teaser: "Signaler une inquiétude à la vie scolaire sans stigmatiser l'élève.",
    type: "prompt",
    prompt: "J'ai une inquiétude concernant un élève (comportement, absentéisme, baisse de résultats, signes de mal-être). Rédige un signalement à la vie scolaire / CPE / infirmière scolaire qui soit : (1) factuel (pas de jugement), (2) daté et précis (événements concrets observés), (3) nuancé (pas de dramatisation ni minimisation), (4) orienté action (proposer un suivi, un rendez-vous). Ton : professionnel, respectueux de la vie privée de l'élève et de sa famille.",
  },
  {
    id: 37,
    self_contained: true,
    chapter: "bulletins",
    title: "Rédiger un mot d'excuse diplomate pour un parent",
    teaser: "Quand un parent demande un courrier pour justifier une absence atypique.",
    type: "prompt",
    prompt: "Un parent me demande de rédiger un courrier pour accompagner une demande d'autorisation d'absence [MOTIF]. Rédige ce courrier : (1) formel mais chaleureux, (2) factuel sur les motifs, (3) rassurant sur le rattrapage scolaire, (4) terminant par une proposition d'accompagnement de l'élève. 200 mots maximum, mise en forme type lettre officielle.",
  },
  {
    id: 38,
    self_contained: true,
    chapter: "bulletins",
    title: "Préparer la synthèse d'une classe pour un conseil",
    teaser: "Vue d'ensemble de la classe en 10 lignes : ambiance, dynamique, points de vigilance.",
    type: "prompt",
    prompt: "Prépare une synthèse de ma classe de [NIVEAU], composée de [EFFECTIF] élèves, pour le conseil de classe du 1er trimestre. Structure : (1) ambiance générale (dynamique, climat, participation), (2) 3 points positifs, (3) 2 points de vigilance, (4) nom des élèves qui méritent une attention particulière (en difficulté ou en progression remarquable), (5) demandes à faire en conseil (soutien scolaire, PAP, suivi infirmerie…). 15 lignes maximum.",
  },
  {
    id: 39,
    chapter: "bulletins",
    title: "Transformer un mail de reproche en dialogue constructif",
    teaser: "L'IA réécrit votre mail à chaud en version professionnelle et apaisée.",
    type: "prompt",
    prompt: "Voici le mail que j'ai écrit à chaud à un parent (il contient de la frustration, des reproches). Réécris-le en version professionnelle : (1) retire les jugements de valeur, (2) remplace les reproches par des faits datés, (3) propose des solutions concrètes à la fin, (4) maintiens le fond du message (il faut que le parent comprenne la gravité) mais sans agressivité. Vérifie que la nouvelle version pourrait être lue à l'inspection sans gêne.",
  },
  {
    id: 40,
    self_contained: true,
    chapter: "bulletins",
    title: "Rédiger un bilan annuel pour un élève à besoins particuliers",
    teaser: "Bilan complet des progrès, aménagements, perspectives pour l'orientation.",
    type: "prompt",
    prompt: "Rédige un bilan annuel pour un élève avec PAP/PPS en [NIVEAU]. Structure : (1) rappel du contexte et des aménagements mis en place, (2) progrès observés dans l'année (par compétence), (3) difficultés persistantes et pistes de remédiation, (4) adaptations qui ont fonctionné (à reconduire), (5) adaptations à ajuster l'an prochain, (6) perspectives d'orientation. Ton : factuel, nuancé, orienté solutions. 1 page A4.",
  },

  // ============== CHAPITRE 5 — CARRIÈRE / PPCR (10 cas) ==============
  {
    id: 41,
    self_contained: true,
    chapter: "carriere",
    title: "Préparer un rendez-vous de carrière PPCR",
    teaser: "Grille d'auto-positionnement + script d'entretien pour chaque compétence.",
    type: "prompt",
    prompt: "Je prépare mon rendez-vous de carrière PPCR (6e, 8e ou 9e année). Génère : (1) une auto-évaluation guidée sur les 11 compétences professionnelles de l'enseignant (donne les intitulés officiels), (2) pour chacune, 2 exemples concrets de pratiques que je peux citer, (3) les 3 questions probables de l'inspecteur AVEC pour chacune une proposition de réponse en 4-6 lignes dans le registre attendu, (4) les formulations à éviter (langue de bois, humilité excessive), (5) comment présenter mes projets d'évolution professionnelle.",
  },
  {
    id: 42,
    self_contained: true,
    chapter: "carriere",
    title: "Rédiger un rapport d'activité pour l'inspection",
    teaser: "Trame complète prête à remplir : contexte, projets, résultats, perspectives.",
    type: "prompt",
    prompt: "Je dois rédiger un rapport d'activité de 4 pages pour mon inspection pédagogique. Structure attendue : (1) présentation du contexte (établissement, classes, publics), (2) mes 3 projets pédagogiques phares de l'année avec résultats mesurables, (3) ma pratique de la différenciation (illustrer avec 2 exemples concrets), (4) ma formation continue suivie, (5) mes perspectives pour l'année suivante. Ton : professionnel, factuel, chiffré quand possible.",
  },
  {
    id: 43,
    self_contained: true,
    chapter: "carriere",
    title: "Préparer un dossier de mutation inter-académique",
    teaser: "Lettre de motivation, barème INEMA, pièces, argumentaire pour la commission.",
    type: "prompt",
    prompt: "J'envisage une demande de mutation inter-académique. Produis : (1) la liste des pièces à préparer, (2) une lettre de motivation type (rapprochement familial / projet pédagogique / raison de santé), (3) comment calculer mon barème INEMA (points d'ancienneté, de rapprochement, de handicap), (4) 3 formulations à utiliser dans la case commentaires, (5) le calendrier des étapes clés.",
  },
  {
    id: 44,
    self_contained: true,
    chapter: "carriere",
    title: "Rédiger une candidature à un poste à profil (PACD, APFL…)",
    teaser: "Lettre de motivation ciblée + argumentaire + préparation à l'entretien.",
    type: "prompt",
    prompt: "Je candidate à un poste à profil [TYPE] dans l'académie de [X]. Rédige : (1) une lettre de motivation de 1 page centrée sur mes atouts spécifiques pour ce poste, (2) un argumentaire listant mes 5 compétences clés pour ce profil (avec exemples concrets), (3) la préparation aux 10 questions probables de l'entretien AVEC pour chacune une proposition de réponse en 4-6 lignes dans le registre attendu, (4) les questions pertinentes que je peux poser au jury (avec en bonus l'intention derrière chacune pour les choisir en situation).",
  },
  {
    id: 45,
    self_contained: true,
    chapter: "carriere",
    title: "Préparer le CAPPEI (certif. aide pédagogique spécialisée)",
    teaser: "Dossier professionnel, choix du module, thème de mémoire pertinent.",
    type: "prompt",
    prompt: "Je prépare le CAPPEI (Certificat d'Aptitude Professionnelle aux Pratiques de l'Éducation Inclusive). Aide-moi à : (1) choisir mon module de spécialisation (parmi les 6 modules), (2) construire mon dossier professionnel (plan en 4 parties), (3) identifier un thème de mémoire pertinent et faisable sur 1 an, (4) la bibliographie incontournable (5 ouvrages de référence), (5) la méthodologie d'enquête-terrain réaliste.",
  },
  {
    id: 46,
    self_contained: true,
    chapter: "carriere",
    title: "Rédiger un projet pour une classe à horaire aménagé",
    teaser: "Projet pédagogique prêt à soumettre au conseil d'administration.",
    type: "prompt",
    prompt: "Je veux proposer une classe à horaire aménagé [THEME] dans mon établissement. Rédige : (1) la note d'intention de 2 pages pour le CA, (2) le projet pédagogique détaillé (objectifs, publics, moyens, évaluation), (3) le budget prévisionnel sur 3 ans, (4) les partenariats possibles, (5) les arguments pour convaincre le chef d'établissement, les parents et les collègues.",
  },
  {
    id: 47,
    self_contained: true,
    chapter: "carriere",
    title: "Préparer une formation à proposer à ses collègues",
    teaser: "Trame de session de 2h : objectifs, déroulé, supports, auto-évaluation.",
    type: "prompt",
    prompt: "Je veux animer une formation de 2 heures sur [THEME] auprès de mes collègues de [DISCIPLINE]. Prépare : (1) les 3 objectifs de la formation, (2) le déroulé minuté (accroche, apports, activité, synthèse), (3) les supports à préparer (slides, document, outil interactif), (4) 3 activités de groupe (icebreaker, atelier, débrief), (5) une grille d'auto-évaluation à distribuer en fin de session.",
  },
  {
    id: 48,
    self_contained: true,
    chapter: "carriere",
    title: "Rédiger un article pour une revue professionnelle",
    teaser: "Structure académique : problématique, dispositif, résultats, pistes.",
    type: "prompt",
    prompt: "Je veux rédiger un article de 6 pages pour [REVUE] sur ma pratique [THEME]. Produis : (1) une problématique accrocheuse, (2) un plan en 4 parties, (3) l'introduction (300 mots), (4) 3 encadrés méthodologiques à insérer, (5) une bibliographie en 10 références. Ton : réflexif, nuancé, ancré dans la pratique.",
  },
  {
    id: 49,
    self_contained: true,
    chapter: "carriere",
    title: "Préparer un oral d'agrégation / CAFEP interne",
    teaser: "Analyse de sujet, plan, références, questions probables du jury.",
    type: "prompt",
    prompt: "Je prépare l'oral d'agrégation interne (ou CAFEP) en [DISCIPLINE]. Sujet imposé : [SUJET]. Produis : (1) une analyse de la problématisation du sujet (3 angles possibles), (2) un plan en 2 ou 3 parties avec idée directrice de chaque partie, (3) les 5 références bibliographiques incontournables à citer, (4) les 10 questions probables du jury avec pistes de réponse, (5) la gestion du temps (préparation 2h, exposé 30 min, entretien 30 min).",
  },
  {
    id: 50,
    self_contained: true,
    chapter: "carriere",
    title: "Monter un dossier pour les Palmes académiques",
    teaser: "Argumentaire, pièces, lettre de soutien à faire rédiger par la hiérarchie.",
    type: "prompt",
    prompt: "Je souhaite être proposé(e) aux Palmes académiques (ou on me l'a proposé). Aide-moi à : (1) comprendre les critères officiels et les 3 grades (Chevalier, Officier, Commandeur), (2) rédiger un argumentaire valorisant mon parcours (10 ans ou plus), (3) la liste des pièces à joindre, (4) une trame de lettre de soutien à faire rédiger par mon chef d'établissement ou mon IPR, (5) le calendrier des remises (14 juillet, rentrée scolaire).",
  },
];

// Helper: filter by chapter and type
export const filterCas = ({ chapter, type }) => {
  return CAS.filter((c) => {
    if (chapter && chapter !== "all" && c.chapter !== chapter) return false;
    if (type && type !== "all" && c.type !== type) return false;
    return true;
  });
};

// Stats for the header
export const STATS = {
  total: CAS.length,
  testable: CAS.filter((c) => c.type === "testable").length,
  prompt: CAS.filter((c) => c.type === "prompt").length,
};

// Number of free cases exposed daily (rotation). The remaining
// (CAS.length - FREE_CASES_QUOTA) are locked until the first credit-pack purchase.
export const FREE_CASES_QUOTA = 5;

/**
 * Deterministic daily rotation returning a Set of case IDs that are
 * free today. Seed is based on YYYY-MM-DD so every visitor sees the
 * same 5 cases on a given day, and cases rotate at midnight.
 *
 * Shared helper so /enseignants/cas-pratiques (grid) and / (Dashboard
 * FreeCasesStrip) stay in sync — same 5 free cases everywhere.
 */
export function pickFreeCaseIds() {
  const allIds = CAS.map((c) => c.id);
  const today = new Date().toISOString().slice(0, 10);
  const seed = [...today].reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
  const shuffled = [...allIds].sort((a, b) => {
    const ka = ((a * 9301 + seed) % 233280) / 233280;
    const kb = ((b * 9301 + seed) % 233280) / 233280;
    return ka - kb;
  });
  return new Set(shuffled.slice(0, FREE_CASES_QUOTA));
}
